import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Send, Upload, X, FileText, CheckCircle2, Phone, Mail, MapPin, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { track } from "../lib/track";
import { Seo, breadcrumbSchema } from "../components/site/Seo";
import { PHONE_DISPLAY, PHONE_HREF, EMAIL } from "../components/site/ContactActions";

const BUYER_TYPES = ["Government Agency", "Prime Contractor / Subcontractor", "Commercial Business", "Healthcare Organization", "Other"];
const ALLOWED_EXT = ["pdf", "doc", "docx", "xls", "xlsx", "csv", "jpg", "jpeg", "png", "zip", "txt"];
const EMPTY = { name: "", company: "", email: "", phone: "", buyer_type: "", message: "", delivery_location: "", deadline: "", solicitation_number: "", nsn: "", part_number: "", cage_code: "", manufacturer: "", response_deadline: "", website: "" };
const FED_INQ = [["solicitation_number", "Solicitation Number"], ["nsn", "NSN"], ["part_number", "Part Number"], ["cage_code", "CAGE"], ["manufacturer", "Manufacturer"], ["response_deadline", "Response Deadline", "date"]];

function Field({ label, id, children, hint }) {
  return (
    <div>
      <label className="pg-label" htmlFor={id}>{label}</label>
      {children}
      {hint && <p className="mt-1 text-[12px] text-slate-500">{hint}</p>}
    </div>
  );
}

export function InquiryForm() {
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInput = useRef(null);

  const handle = (k) => (e) => { track("inquiry_started", {}, true); setForm((f) => ({ ...f, [k]: e.target.value })); };

  const addFiles = (list) => {
    const next = [...files];
    for (const f of list) {
      const ext = f.name.includes(".") ? f.name.split(".").pop().toLowerCase() : "";
      if (!ALLOWED_EXT.includes(ext)) { toast.error(`${f.name}: file type not allowed`); continue; }
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: exceeds 10MB limit`); continue; }
      if (next.length >= 3) { toast.error("Maximum 3 files"); break; }
      next.push(f);
    }
    setFiles(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 5) {
      toast.error("Please complete your name, email, and a brief description of what you need.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/contact", form);
      let uploaded = 0;
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        try { await api.post(`/contact/${data.id}/documents`, fd); uploaded += 1; }
        catch { toast.error(`Upload failed for ${f.name}`); }
      }
      setResult({ reference: data.reference, uploaded, total: files.length });
      track("inquiry_submitted", { reference: data.reference });
      setForm(EMPTY); setFiles([]);
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map((x) => x.msg).join("; ") : d;
      toast.error(typeof msg === "string" ? msg : `Submission failed. Please try again or call ${PHONE_DISPLAY}.`);
    } finally { setSubmitting(false); }
  };

  if (result) {
    return (
      <div data-testid="inquiry-success-panel" className="bg-white border border-slate-200 rounded-2xl p-8 text-center">
        <div className="w-14 h-14 mx-auto rounded-full bg-[var(--pg-accent)]/10 text-[var(--pg-accent)] flex items-center justify-center"><CheckCircle2 size={28} /></div>
        <div className="mt-4 font-[Poppins] font-bold text-[12px] tracking-[0.2em] uppercase text-[var(--pg-accent)]">Inquiry Received</div>
        <div className="mt-2 font-[Poppins] font-extrabold text-[22px] text-[var(--pg-navy)]" data-testid="inquiry-reference">Reference: {result.reference}</div>
        <p className="mt-3 text-[14.5px] text-[var(--pg-ink-soft)]">
          A Patrick Global procurement specialist will respond within one business day. A confirmation email is on its way.
          {result.total > 0 && ` ${result.uploaded} of ${result.total} file(s) attached.`}
        </p>
        <button data-testid="inquiry-send-another" onClick={() => setResult(null)} className="btn-primary mt-6">Send Another Message</button>
      </div>
    );
  }

  return (
    <form data-testid="inquiry-form" onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 text-left" aria-label="Short inquiry form">
      <input type="text" value={form.website} onChange={handle("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Full Name *" id="inq-name"><input id="inq-name" data-testid="inquiry-name-input" className="pg-input" value={form.name} onChange={handle("name")} required autoComplete="name" /></Field>
        <Field label="Agency / Company" id="inq-company"><input id="inq-company" data-testid="inquiry-company-input" className="pg-input" value={form.company} onChange={handle("company")} autoComplete="organization" /></Field>
        <Field label="Email *" id="inq-email"><input id="inq-email" data-testid="inquiry-email-input" type="email" className="pg-input" value={form.email} onChange={handle("email")} required autoComplete="email" /></Field>
        <Field label="Phone" id="inq-phone"><input id="inq-phone" data-testid="inquiry-phone-input" type="tel" className="pg-input" value={form.phone} onChange={handle("phone")} autoComplete="tel" /></Field>
        <Field label="I am a..." id="inq-buyer-type">
          <select id="inq-buyer-type" data-testid="inquiry-buyer-type-select" className="pg-input" value={form.buyer_type} onChange={handle("buyer_type")}>
            <option value="">Select one...</option>
            {BUYER_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </Field>
        <Field label="Needed By" id="inq-deadline"><input id="inq-deadline" data-testid="inquiry-deadline-input" type="date" className="pg-input" value={form.deadline} onChange={handle("deadline")} /></Field>
        <div className="sm:col-span-2">
          <Field label="Delivery Location" id="inq-location"><input id="inq-location" data-testid="inquiry-location-input" className="pg-input" value={form.delivery_location} onChange={handle("delivery_location")} placeholder="City, State (e.g., Sugar Land, TX)" /></Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="What do you need? *" id="inq-message" hint="Items, quantities, specifications, solicitation number, or a brief description of the requirement.">
            <textarea id="inq-message" data-testid="inquiry-message-input" rows={5} className="pg-input resize-y" value={form.message} onChange={handle("message")} required />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <details data-testid="inquiry-federal-details" className="group border border-slate-200 rounded-xl bg-[var(--pg-bg)]" open={form.buyer_type === "Government Agency" || form.buyer_type === "Prime Contractor / Subcontractor"}>
            <summary className="cursor-pointer list-none px-4 py-3 font-[Poppins] font-semibold text-[13.5px] text-[var(--pg-navy)] flex items-center justify-between">
              Federal / solicitation details (optional) <span className="text-[var(--pg-accent)] text-[12px]">expand</span>
            </summary>
            <div className="px-4 pb-4 grid sm:grid-cols-3 gap-3">
              {FED_INQ.map(([k, label, type]) => (
                <Field key={k} label={label} id={`inq-${k}`}><input id={`inq-${k}`} data-testid={`inquiry-${k}-input`} type={type || "text"} className="pg-input" value={form[k]} onChange={handle(k)} /></Field>
              ))}
              <p className="sm:col-span-3 text-[12.5px] text-[var(--pg-ink-soft)]">Have a full solicitation with line items? <Link to="/request-a-quote?type=federal" data-testid="inquiry-federal-rfq-link" className="font-semibold text-[var(--pg-navy)] hover:text-[var(--pg-accent)]">Submit a Detailed Federal RFQ →</Link></p>
            </div>
          </details>
        </div>
        <div className="sm:col-span-2">
          <label className="pg-label" htmlFor="inq-files">Attach a solicitation, bid tab, or spec sheet (optional · up to 3 files · 10MB each)</label>
          <input id="inq-files" ref={fileInput} type="file" multiple className="hidden" data-testid="inquiry-file-input"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.zip,.txt"
            onChange={(e) => { addFiles(Array.from(e.target.files || [])); e.target.value = ""; }} />
          <button type="button" data-testid="inquiry-file-upload-button" onClick={() => fileInput.current?.click()}
            className="mt-1 w-full border-2 border-dashed border-slate-300 hover:border-[var(--pg-accent)] rounded-xl py-4 flex items-center justify-center gap-2 text-[var(--pg-ink-soft)] hover:text-[var(--pg-accent)] transition-colors text-[13.5px] font-[Poppins] font-medium">
            <Upload size={18} /> Click to attach files
          </button>
          {files.length > 0 && (
            <ul className="mt-3 space-y-2" data-testid="inquiry-file-list">
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`} className="flex items-center justify-between bg-[var(--pg-bg)] rounded-lg px-3 py-2 text-[13.5px]">
                  <span className="flex items-center gap-2 truncate"><FileText size={15} className="text-[var(--pg-accent)]" />{f.name}</span>
                  <button type="button" aria-label={`Remove ${f.name}`} data-testid={`inquiry-file-remove-${i}`} onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500 p-1"><X size={15} /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
        <p className="text-[12.5px] text-[var(--pg-ink-soft)] max-w-md">
          By submitting, you agree to be contacted by Patrick Global regarding your inquiry. We do not share your details.
        </p>
        <button data-testid="inquiry-submit-button" type="submit" disabled={submitting} className="btn-primary disabled:opacity-70 disabled:cursor-not-allowed">
          {submitting ? <><Loader2 className="animate-spin" size={18} /> Sending...</> : <>Send Inquiry <Send size={16} /></>}
        </button>
      </div>
    </form>
  );
}

function ContactCard({ icon: Icon, title, children, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-xl p-5 flex items-start gap-4">
      <div className="w-11 h-11 rounded-lg bg-[var(--pg-bg)] text-[var(--pg-accent)] flex items-center justify-center flex-shrink-0"><Icon size={20} /></div>
      <div>
        <div className="font-[Poppins] font-semibold text-[14px] text-[var(--pg-navy)]">{title}</div>
        <div className="text-[14.5px] text-[var(--pg-ink-soft)] mt-0.5 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

export default function ContactPage() {
  return (
    <div data-testid="contact-page">
      <Seo route="/contact" jsonLd={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }])} />
      <section className="pt-[130px] pb-14 hero-bg">
        <div className="pg-container max-w-4xl">
          <span className="eyebrow" style={{ color: "#7EC4FF" }}>Contact</span>
          <h1 className="mt-5 text-[36px] md:text-[50px] font-extrabold leading-[1.06] text-white">Talk to a Procurement Specialist</h1>
          <p className="mt-5 text-[17px] text-white/80 max-w-2xl leading-relaxed">
            Quick question, a solicitation you'd like us to review, or a requirement you want priced? Send a short
            message below, or use the detailed RFQ form when you have quantities and specifications ready.
          </p>
        </div>
      </section>
      <section className="py-14 md:py-20 bg-[var(--pg-bg)]">
        <div className="pg-container grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4 space-y-4">
            <ContactCard icon={Phone} title="Call" testid="contact-card-phone">
              <a href={PHONE_HREF} data-testid="contact-page-call" onClick={() => track("phone_click", { source: "contact_page" })} className="font-semibold text-[var(--pg-navy)] hover:text-[var(--pg-accent)]">{PHONE_DISPLAY}</a>
            </ContactCard>
            <ContactCard icon={Mail} title="Email" testid="contact-card-email">
              <a href={`mailto:${EMAIL}`} data-testid="contact-page-email" onClick={() => track("email_click", { source: "contact_page" })} className="font-semibold text-[var(--pg-navy)] hover:text-[var(--pg-accent)]">{EMAIL}</a>
            </ContactCard>
            <ContactCard icon={MapPin} title="Headquarters" testid="contact-card-address">
              8700 Commerce Park Drive, Suite 238<br />Houston, Texas 77036
            </ContactCard>
            <Link to="/request-a-quote?type=federal" data-testid="contact-federal-rfq-cta" className="btn-primary w-full justify-center">Submit a Detailed Federal RFQ <ArrowRight size={16} /></Link>
            <div className="bg-[var(--pg-navy)] text-white rounded-xl p-5" data-testid="contact-rfq-card">
              <div className="font-[Poppins] font-bold text-[15px]">Have a full requirement?</div>
              <p className="mt-1.5 text-[13.5px] text-white/80 leading-relaxed">Use the detailed RFQ form to include quantities, part numbers, specifications, delivery location, and up to five documents.</p>
              <Link to="/request-a-quote" data-testid="contact-page-rfq-link" className="mt-4 inline-flex items-center gap-2 text-[13.5px] font-[Poppins] font-semibold text-[#7EC4FF] hover:text-white">Open the RFQ form <ArrowRight size={15} /></Link>
            </div>
          </div>
          <div className="lg:col-span-8"><InquiryForm /></div>
        </div>
      </section>
    </div>
  );
}
